import { TriggerPoint } from "pages/survey/surveyService";
import { getInAppLeavePath } from "pages/survey/exit/getInAppLeavePath";
import { useEffect, useState } from "react";

const PREVIOUS_QUESTION_SELECTOR = ".previous-question-button";
const LEAVE_CONTROL_SELECTOR = `a, [data-exit-survey-href]`;

function hasExitSurveyTrap(state: unknown): boolean {
  return typeof state === "object" && state !== null && "exitSurveyTrap" in state;
}

function pushExitSurveyTrap() {
  const state: unknown = globalThis.history.state;
  if (hasExitSurveyTrap(state)) {
    return;
  }
  globalThis.history.pushState({ exitSurveyTrap: true }, "", globalThis.location.href);
}

export const useExitIntentHandler = (exitSurveySessionkey: string, isSurveyDisplayableStepcode: boolean) => {
  const [exitIntent, setExitIntent] = useState(false);
  const [triggerPoint, setTriggerPoint] = useState<TriggerPoint | null>(null);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [pendingLogout, setPendingLogout] = useState(false);

  const checkIsSurveyDisplayed = () => {
    return sessionStorage.getItem(exitSurveySessionkey) === "true";
  };

  const trapNavigateAway = (leavePath: string, isLogout: boolean) => {
    setNextUrl(leavePath);
    setPendingLogout(isLogout);
    setTriggerPoint(TriggerPoint.NavigateAway);
    setExitIntent(true);
  };

  useEffect(() => {
    if (checkIsSurveyDisplayed() || !isSurveyDisplayableStepcode) {
      return;
    }

    pushExitSurveyTrap();

    const handlePopState = () => {
      if (checkIsSurveyDisplayed()) {
        return;
      }

      pushExitSurveyTrap();
      setNextUrl(null);
      setPendingLogout(false);
      setTriggerPoint(TriggerPoint.BrowserBack);
      setExitIntent(true);
    };

    globalThis.addEventListener("popstate", handlePopState);

    return () => globalThis.removeEventListener("popstate", handlePopState);
  }, [isSurveyDisplayableStepcode]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (checkIsSurveyDisplayed()) {
        return;
      }

      const target = event.target as HTMLElement;
      if (target.closest(PREVIOUS_QUESTION_SELECTOR)) {
        return;
      }

      const leaveControl = target.closest(LEAVE_CONTROL_SELECTOR);
      if (!(leaveControl instanceof HTMLElement)) return;

      const href =
        leaveControl instanceof HTMLAnchorElement
          ? leaveControl.getAttribute("href")
          : leaveControl.dataset.exitSurveyHref;
      const leavePath = getInAppLeavePath(href, globalThis.location.pathname);
      if (!leavePath) return;

      if (isSurveyDisplayableStepcode) {
        event.preventDefault();
        event.stopPropagation();
        trapNavigateAway(leavePath, leaveControl.dataset.exitSurveyLogout === "true");
      }
    };

    document.addEventListener("click", handleClick, true);

    return () => document.removeEventListener("click", handleClick, true);
  }, [isSurveyDisplayableStepcode]);

  return { exitIntent, triggerPoint, nextUrl, pendingLogout };
};
